import {
  type FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import dayjs from 'dayjs';
import {action, runInAction} from 'mobx';
import {Observer} from 'mobx-react';

import type {EditionApiModel as Edition} from '@yourcoach/shared/api/program';

import {useI18n} from '@yourcoach/i18n';
import {getSecondsFromMidnight} from '@yourcoach/shared/utils/datetime';
import {
  EVERGREEN_PROGRAM_SCHEDULE_DURATION,
  isEditionEvergreen,
} from '@yourcoach/shared/utils/entity/program';
import {validateFields} from '@yourcoach/shared/utils/validation';
import {createHtmlInputField} from '@yourcoach/shared/utils/validation/createHtmlInputField';

import Button from '@src/components/Button';
import TextField from '@src/components/TextField';

import type {ProgramT} from '.';

import styles from './MaterialPublicationDay.module.css';

export interface Props {
  material: ScheduledMaterial;
  onSuccess: (material: ScheduledMaterial) => void;
  program: ProgramT;
}

type ScheduledMaterial = Edition['materials'][0];

const CRUProgramMaterialPublicationDay: FC<Props> = ({
  material,
  onSuccess,
  program,
}) => {
  const {t} = useI18n();
  const [date, setDate] = useState(new Date());

  const lastEdition = useMemo(
    () => program.editions.slice().pop(),
    [program.editions],
  );

  const programDuration = useMemo(() => {
    if (isEditionEvergreen(lastEdition)) {
      return EVERGREEN_PROGRAM_SCHEDULE_DURATION;
    }

    return lastEdition?.duration || 1;
  }, [lastEdition]);

  const day = useRef(0);

  const fields = useRef({
    day: createHtmlInputField('day', {
      customValidate: action(() => {
        const field = fields.day;

        let isValid = true;

        field.error = null;

        if (!field.value) {
          field.error = t(
            'shared:CRUProgramMaterialPublicationDay.day_required_error_message',
          );
          isValid = false;
        } else if (day.current > programDuration - 1) {
          field.error = t(
            'shared:CRUProgramMaterialPublicationDay.day_max_error_message',
            {
              value: programDuration,
            },
          );
          isValid = false;
        }

        field.isValid = isValid;

        return isValid;
      }),
      validationRule: {
        type: 'string',
      },
    }),
  }).current;

  useEffect(() => {
    day.current = material.day;

    setDate(dayjs(date).startOf('day').add(material.time, 'second').toDate());

    runInAction(() => {
      fields.day.value = `${day.current + 1}`;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDayChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let dayText = e.currentTarget.value.replace(/\D/g, '');

      if (dayText === '0') {
        dayText = '';
      }

      day.current = dayText ? parseInt(dayText, 10) : 0;

      if (day.current > 0) {
        day.current = day.current - 1;
      }

      fields.day.setValue(dayText);
      fields.day.validate();
    },
    [fields.day],
  );

  const onTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const timeString = e.currentTarget.value;

    if (timeString) {
      const [hours, mins] = timeString.split(':');

      setDate(prev =>
        dayjs(prev)
          .startOf('day')
          .add(parseInt(hours || '0', 10) || 0, 'hours')
          .add(parseInt(mins || '0', 10) || 0, 'minutes')
          .toDate(),
      );
    }
  }, []);

  const onSetButtonClick = useCallback(async () => {
    if (validateFields(fields)) {
      onSuccess({
        ...material,
        day: day.current,
        time: getSecondsFromMidnight(date),
      });
    }
  }, [date, fields, material, onSuccess]);

  return (
    <>
      <div
        className={classNames(
          'CRUProgramMaterialPublicationDay',
          styles.Component,
        )}
      >
        <div className={styles.container}>
          <h3>{`${t('shared:CRUProgramMaterialPublicationDay.day_label')}:`}</h3>
          <Observer>
            {() => (
              <TextField
                error={fields.day.error || ''}
                onChange={onDayChange}
                placeholder={t(
                  'shared:CRUProgramMaterialPublicationDay.day_text_input_placeholder',
                  {
                    max_day: programDuration,
                    min_day: 1,
                  },
                )}
                value={fields.day.value}
              />
            )}
          </Observer>
        </div>
        <div className={styles.container}>
          <h3>{`${t('shared:CRUProgramMaterialPublicationDay.time_label')}:`}</h3>
          <TextField
            onChange={onTimeChange}
            type="time"
            value={dayjs(date).format('HH:mm')}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          className={classNames({
            [styles.actionButton!]: true,
          })}
          onClick={onSetButtonClick}
          type="button"
        >
          {t('shared:confirm')}
        </Button>
      </div>
    </>
  );
};

export default memo(CRUProgramMaterialPublicationDay);
